/**
 * CreateLeadModal — Modal fuer manuelle Lead-Anlage (BOARD-06, BOARD-07).
 *
 * Trigger: store.isCreateModalOpen=true via Plus-Button in Column.tsx (Spalte "In Kontakt").
 *
 * Felder (7):
 *  1. Vereinsname (Pflicht)
 *  2. Telefon (Pflicht ODER E-Mail)
 *  3. E-Mail (Pflicht ODER Telefon)
 *  4. Ansprechpartner (Pflicht)
 *  5. Zugewiesen an (Dropdown, Default: currentUserId aus Core-Auth-Store)
 *  6. Tags (Multi-Input mit Enter)
 *  7. Notiz (Textarea)
 *
 * Validation Client-Side via validateNewLead pure function (BOARD-07).
 * Backend-Call: POST /leads mit source='manual', status='in_kontakt',
 * autoWorkflowActive=false.
 *
 * Polling pausiert waehrend Modal offen — Store-Flag `isCreateModalOpen=true`
 * wird von useLeadBoard.startPolling beruecksichtigt.
 *
 * CI-Farben: #E9FF57 (Submit), #1A1A1A (Text), #DAD9D9 (Border).
 * NIE weisse Schrift auf gelbem Hintergrund.
 */

import { useEffect, useState, type FormEvent, type KeyboardEvent } from 'react';
import { X, Plus as PlusIcon } from 'lucide-react';
import { useAuthStore } from '@libero/core/stores/authStore';
import { useLeadBoard } from '../store/useLeadBoard';
import {
  validateNewLead,
  type NewLeadForm,
  type ValidationError,
} from '../lib/validateNewLead';

function makeEmptyForm(currentUserId: number | null): NewLeadForm {
  return {
    vereinName: '',
    vereinEmail: '',
    vereinPhone: '',
    vereinContactPerson: '',
    assignedUserId: currentUserId,
    tags: [],
    notes: '',
  };
}

export default function CreateLeadModal() {
  const isOpen = useLeadBoard((s) => s.isCreateModalOpen);
  const closeCreateModal = useLeadBoard((s) => s.closeCreateModal);
  const createLead = useLeadBoard((s) => s.createLead);
  const users = useLeadBoard((s) => s.users);

  // Default-User aus Core-Auth-Store (BOARD-06 / CARD-06 sinngemaess).
  // Fallback null wenn nicht angemeldet — User kann ueber Dropdown setzen.
  const currentUser = useAuthStore((s) => s.user);
  const currentUserId = currentUser?.id ?? null;

  const [form, setForm] = useState<NewLeadForm>(() =>
    makeEmptyForm(currentUserId),
  );
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState('');

  // Beim Oeffnen Form mit aktuellem User neu seeden — verhindert dass ein
  // alter Default-Wert nach Logout/Login haengen bleibt.
  useEffect(() => {
    if (isOpen) {
      setForm(makeEmptyForm(currentUserId));
      setErrors([]);
      setTagInput('');
    }
  }, [isOpen, currentUserId]);

  if (!isOpen) return null;

  const errorFor = (field: string): string | undefined =>
    errors.find((e) => e.field === field)?.message;

  const handleClose = () => {
    setForm(makeEmptyForm(currentUserId));
    setErrors([]);
    setTagInput('');
    closeCreateModal();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validateNewLead(form);
    setErrors(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    try {
      // Submit mit normalisierten Strings + Pflicht-Defaults fuer manuelle Anlage.
      // source='manual', status='in_kontakt', autoWorkflowActive=false sind
      // BOARD-06-Konvention: manueller Lead startet in der "In Kontakt"-Spalte
      // ohne Auto-Workflow.
      await createLead({
        vereinName: form.vereinName.trim(),
        vereinEmail: form.vereinEmail.trim() || null,
        vereinPhone: form.vereinPhone.trim() || null,
        vereinContactPerson: form.vereinContactPerson.trim(),
        assignedUserId: form.assignedUserId,
        tags: form.tags,
        notes: form.notes.trim() || null,
        source: 'manual',
        status: 'in_kontakt',
        autoWorkflowActive: false,
      } as any);
      handleClose();
    } catch (err: any) {
      setErrors([
        {
          field: '_form',
          message: err?.message ?? 'Anlegen fehlgeschlagen',
        },
      ]);
    } finally {
      setSubmitting(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t.length === 0) return;
    if (!form.tags.includes(t)) {
      setForm({ ...form, tags: [...form.tags, t] });
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setForm({ ...form, tags: form.tags.filter((x) => x !== tag) });
  };

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] flex flex-col mx-4"
      >
        {/* Header */}
        <header className="flex items-center justify-between p-4 border-b border-[#DAD9D9]">
          <div className="flex items-center gap-2">
            <PlusIcon className="w-5 h-5 text-[#1A1A1A]" />
            <h2 className="text-lg font-semibold text-[#1A1A1A]">
              Neuen Lead anlegen
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-500 hover:text-[#1A1A1A] p-1 rounded"
            aria-label="Modal schliessen"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {/* 1. Vereinsname */}
          <div>
            <label
              htmlFor="cl-vereinName"
              className="block text-sm font-medium text-[#1A1A1A] mb-1"
            >
              Vereinsname <span className="text-red-600">*</span>
            </label>
            <input
              id="cl-vereinName"
              type="text"
              value={form.vereinName}
              onChange={(e) =>
                setForm({ ...form, vereinName: e.target.value })
              }
              className={
                'w-full border rounded px-3 py-2 text-[#1A1A1A] ' +
                (errorFor('vereinName')
                  ? 'border-red-500'
                  : 'border-[#DAD9D9]')
              }
            />
            {errorFor('vereinName') && (
              <p className="text-red-600 text-xs mt-1">
                {errorFor('vereinName')}
              </p>
            )}
          </div>

          {/* 2. Telefon */}
          <div>
            <label
              htmlFor="cl-vereinPhone"
              className="block text-sm font-medium text-[#1A1A1A] mb-1"
            >
              Telefon
            </label>
            <input
              id="cl-vereinPhone"
              type="tel"
              value={form.vereinPhone}
              onChange={(e) =>
                setForm({ ...form, vereinPhone: e.target.value })
              }
              className={
                'w-full border rounded px-3 py-2 text-[#1A1A1A] ' +
                (errorFor('vereinPhone')
                  ? 'border-red-500'
                  : 'border-[#DAD9D9]')
              }
            />
            {errorFor('vereinPhone') && (
              <p className="text-red-600 text-xs mt-1">
                {errorFor('vereinPhone')}
              </p>
            )}
          </div>

          {/* 3. E-Mail */}
          <div>
            <label
              htmlFor="cl-vereinEmail"
              className="block text-sm font-medium text-[#1A1A1A] mb-1"
            >
              E-Mail
            </label>
            <input
              id="cl-vereinEmail"
              type="email"
              value={form.vereinEmail}
              onChange={(e) =>
                setForm({ ...form, vereinEmail: e.target.value })
              }
              className={
                'w-full border rounded px-3 py-2 text-[#1A1A1A] ' +
                (errorFor('vereinEmail')
                  ? 'border-red-500'
                  : 'border-[#DAD9D9]')
              }
            />
            {errorFor('vereinEmail') && (
              <p className="text-red-600 text-xs mt-1">
                {errorFor('vereinEmail')}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Mindestens Telefon oder E-Mail angeben
            </p>
          </div>

          {/* 4. Ansprechpartner */}
          <div>
            <label
              htmlFor="cl-vereinContactPerson"
              className="block text-sm font-medium text-[#1A1A1A] mb-1"
            >
              Ansprechpartner <span className="text-red-600">*</span>
            </label>
            <input
              id="cl-vereinContactPerson"
              type="text"
              value={form.vereinContactPerson}
              onChange={(e) =>
                setForm({ ...form, vereinContactPerson: e.target.value })
              }
              className={
                'w-full border rounded px-3 py-2 text-[#1A1A1A] ' +
                (errorFor('vereinContactPerson')
                  ? 'border-red-500'
                  : 'border-[#DAD9D9]')
              }
            />
            {errorFor('vereinContactPerson') && (
              <p className="text-red-600 text-xs mt-1">
                {errorFor('vereinContactPerson')}
              </p>
            )}
          </div>

          {/* 5. Zugewiesen an — Default: currentUserId */}
          <div>
            <label
              htmlFor="cl-assignedUserId"
              className="block text-sm font-medium text-[#1A1A1A] mb-1"
            >
              Zugewiesen an
            </label>
            <select
              id="cl-assignedUserId"
              value={form.assignedUserId ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  assignedUserId: e.target.value
                    ? Number(e.target.value)
                    : null,
                })
              }
              className="w-full border border-[#DAD9D9] rounded px-3 py-2 text-[#1A1A1A] bg-white"
            >
              <option value="">– Niemand –</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email}
                </option>
              ))}
            </select>
          </div>

          {/* 6. Tags */}
          <div>
            <label
              htmlFor="cl-tagInput"
              className="block text-sm font-medium text-[#1A1A1A] mb-1"
            >
              Tags
            </label>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {form.tags.map((t) => (
                  <span
                    key={t}
                    className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs flex items-center gap-1"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeTag(t)}
                      className="hover:text-[#1A1A1A]"
                      aria-label={`Tag ${t} entfernen`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              id="cl-tagInput"
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              placeholder="Tag hinzufuegen + Enter"
              className="w-full border border-[#DAD9D9] rounded px-3 py-2 text-[#1A1A1A]"
            />
          </div>

          {/* 7. Notiz */}
          <div>
            <label
              htmlFor="cl-notes"
              className="block text-sm font-medium text-[#1A1A1A] mb-1"
            >
              Notiz
            </label>
            <textarea
              id="cl-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full border border-[#DAD9D9] rounded px-3 py-2 text-[#1A1A1A]"
            />
          </div>

          {/* Form-Level-Error (z.B. von Backend) */}
          {errorFor('_form') && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-3 py-2 rounded text-sm">
              {errorFor('_form')}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-2 p-4 border-t border-[#DAD9D9]">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 rounded border border-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="bg-[#E9FF57] text-[#1A1A1A] px-4 py-2 rounded font-medium hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? 'Anlegen...' : 'Lead anlegen'}
          </button>
        </footer>
      </form>
    </div>
  );
}
